import { useCallback, useEffect, useRef, useState } from 'react';
import { AUTOGUARDADO_DEBOUNCE_MS } from '../constants/presupuestos';
import type { EstadoGuardado } from '../types';

interface UseAutoguardadoItemOptions<TData> {
  /** Persiste los cambios de un ítem. Debe rechazar si la operación falla. */
  guardar: (itemId: string, data: TData) => Promise<unknown>;
  debounceMs?: number;
}

const MS_ESTADO_GUARDADO = 2000;

/**
 * Autoguardado de ítems de presupuesto.
 *
 * - Agrupa los cambios de un mismo ítem y los envía una sola vez tras el debounce.
 * - No emite request si no hay nada que cambiar.
 * - Descarta respuestas viejas: si mientras vuelve una respuesta se disparó otra
 *   para el mismo ítem, la anterior no pisa el estado.
 * - Conserva los cambios fallidos para poder reintentarlos.
 */
export function useAutoguardadoItem<TData extends Record<string, unknown>>({
  guardar,
  debounceMs = AUTOGUARDADO_DEBOUNCE_MS,
}: UseAutoguardadoItemOptions<TData>) {
  const [estados, setEstados] = useState<Record<string, EstadoGuardado>>({});
  const [pendientes, setPendientes] = useState(0);

  const cambiosPendientesRef = useRef<Map<string, TData>>(new Map());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const fallidosRef = useRef<Map<string, TData>>(new Map());
  const secuenciaRef = useRef<Map<string, number>>(new Map());
  const enVueloRef = useRef(0);
  const montadoRef = useRef(true);
  const guardarRef = useRef(guardar);

  guardarRef.current = guardar;

  const recalcularPendientes = useCallback(() => {
    if (!montadoRef.current) return;
    setPendientes(cambiosPendientesRef.current.size + enVueloRef.current);
  }, []);

  const marcarEstado = useCallback((itemId: string, estado: EstadoGuardado) => {
    if (!montadoRef.current) return;
    setEstados((actual) => ({ ...actual, [itemId]: estado }));
  }, []);

  const enviar = useCallback(
    async (itemId: string) => {
      const data = cambiosPendientesRef.current.get(itemId);
      cambiosPendientesRef.current.delete(itemId);
      timersRef.current.delete(itemId);

      if (!data || Object.keys(data).length === 0) {
        recalcularPendientes();
        return;
      }

      const secuencia = (secuenciaRef.current.get(itemId) ?? 0) + 1;
      secuenciaRef.current.set(itemId, secuencia);

      enVueloRef.current += 1;
      recalcularPendientes();
      marcarEstado(itemId, 'guardando');

      try {
        await guardarRef.current(itemId, data);

        // Llegó una respuesta vieja: ya se disparó otro guardado para este ítem.
        if (secuenciaRef.current.get(itemId) !== secuencia) return;

        fallidosRef.current.delete(itemId);
        marcarEstado(itemId, 'guardado');

        setTimeout(() => {
          if (!montadoRef.current) return;
          setEstados((actual) =>
            actual[itemId] === 'guardado' ? { ...actual, [itemId]: 'idle' } : actual
          );
        }, MS_ESTADO_GUARDADO);
      } catch {
        if (secuenciaRef.current.get(itemId) !== secuencia) return;

        // Se conserva lo ingresado para poder reintentarlo.
        fallidosRef.current.set(itemId, data);
        marcarEstado(itemId, 'error');
      } finally {
        enVueloRef.current = Math.max(0, enVueloRef.current - 1);
        recalcularPendientes();
      }
    },
    [marcarEstado, recalcularPendientes]
  );

  /** Registra un cambio y programa su guardado. */
  const programarGuardado = useCallback(
    (itemId: string, data: Partial<TData>) => {
      if (!data || Object.keys(data).length === 0) return;

      const acumulado = {
        ...(cambiosPendientesRef.current.get(itemId) ?? ({} as TData)),
        ...data,
      } as TData;

      cambiosPendientesRef.current.set(itemId, acumulado);

      const timerPrevio = timersRef.current.get(itemId);
      if (timerPrevio) clearTimeout(timerPrevio);

      timersRef.current.set(
        itemId,
        setTimeout(() => void enviar(itemId), debounceMs)
      );

      recalcularPendientes();
    },
    [debounceMs, enviar, recalcularPendientes]
  );

  /** Fuerza el envío inmediato de todo lo pendiente. */
  const guardarAhora = useCallback(async () => {
    const ids = Array.from(cambiosPendientesRef.current.keys());

    ids.forEach((itemId) => {
      const timer = timersRef.current.get(itemId);
      if (timer) clearTimeout(timer);
      timersRef.current.delete(itemId);
    });

    await Promise.all(ids.map((itemId) => enviar(itemId)));
  }, [enviar]);

  /** Reintenta los cambios que quedaron con error. */
  const reintentar = useCallback(async () => {
    const fallidos = Array.from(fallidosRef.current.entries());
    fallidosRef.current.clear();

    fallidos.forEach(([itemId, data]) => {
      cambiosPendientesRef.current.set(itemId, {
        ...data,
        ...(cambiosPendientesRef.current.get(itemId) ?? {}),
      } as TData);
    });

    await Promise.all(fallidos.map(([itemId]) => enviar(itemId)));
  }, [enviar]);

  useEffect(() => {
    montadoRef.current = true;
    const timers = timersRef.current;

    return () => {
      montadoRef.current = false;
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const hayError = Object.values(estados).some((estado) => estado === 'error');
  const hayCambiosPendientes = pendientes > 0;

  // Avisar antes de abandonar la pantalla con cambios sin guardar.
  useEffect(() => {
    if (!hayCambiosPendientes && !hayError) return undefined;

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hayCambiosPendientes, hayError]);

  const estadoGlobal: EstadoGuardado = hayError
    ? 'error'
    : hayCambiosPendientes
      ? 'guardando'
      : Object.values(estados).some((estado) => estado === 'guardado')
        ? 'guardado'
        : 'idle';

  return {
    estados,
    estadoGlobal,
    hayCambiosPendientes,
    hayError,
    programarGuardado,
    guardarAhora,
    reintentar,
  };
}
